import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useEditMode, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useReadableOnPrimary, useReadablePrimaryOnWhite } from '../hooks/useReadableOnPrimary';

/**
 * Lifestyle feature stack — five alternating image/text panels that mirror the
 * flatz520.com lifestyle page: Better-Built Living, Resident Events, Flexible
 * Living Programs, Resident Rewards, and Spotify Channel. Each card is a
 * slot group so operators can swap imagery, copy, and the CTA target per site.
 * Resident Events and Spotify are hidden on the live site until they have a
 * custom (non-contact-page) link; band colors re-alternate around them.
 *
 * NOTE: Each card uses literal `groupPath`/`path` values and inline literal
 * slot group definitions so the schema linter can statically extract them.
 * Factoring the card into a subcomponent would hide the paths from the
 * linter — keep the blocks inline.
 */

// ============================================================================
// Static slot group definitions (literal objects for contentSchema extraction)
// ============================================================================

// Cards 1, 3, and 4 pull property photos via the generator, each with a
// distinct photo type. Their pre-generation defaults use gallery indexes 1–3
// so they don't collide with the Lifestyle hero (index 0) or closing CTA
// (index 8). Cards 2 and 6 default to stock photos (Unsplash); operators can
// swap in licensed or property-owned assets via the editor's media picker.

const feature1ImageDef: SlotGroupDefinition = {
  label: 'Better-Built Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'An exterior photo of this property’s building — the facade, entrance, or the building seen from the street or grounds. Must show the outside of the building, not an interior or amenity close-up.',
    context:
      'Card thumb (card 1 of 5, Better-Built Living) on the Lifestyle page. Must be a different photo from the Lifestyle hero background, the closing CTA background, and the Flexible Living (interior) and Resident Rewards (community) cards.',
  },
};

const feature2ImageDef: SlotGroupDefinition = {
  label: 'Events Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Friends laughing together over a shared meal' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const feature3ImageDef: SlotGroupDefinition = {
  label: 'Flexible Living Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'An interior photo of a residence at this property — a living room, kitchen, bedroom, or other in-unit space. Must be inside an apartment home, not a shared amenity or the building exterior.',
    context:
      'Card thumb (card 3 of 5, Flexible Living Programs) on the Lifestyle page. Must be a different photo from the Lifestyle hero background, the closing CTA background, and the Better-Built Living (exterior) and Resident Rewards (community) cards.',
  },
};

const feature4ImageDef: SlotGroupDefinition = {
  label: 'Rewards Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A community photo of this property — a shared amenity, common area, courtyard, or grounds that residents use together. Pick whatever community space this property actually has; not an in-unit interior or a plain building facade.',
    context:
      'Card thumb (card 4 of 5, Resident Rewards) on the Lifestyle page. Must be a different photo from the Lifestyle hero background, the closing CTA background, and the Better-Built Living (exterior) and Flexible Living (interior) cards.',
  },
};

const feature6ImageDef: SlotGroupDefinition = {
  label: 'Spotify Channel Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=80',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'DJ mixing music under neon lights' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const feature1LinkDef: SlotGroupDefinition = {
  label: 'Card 1 Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Be Informed' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const feature2LinkDef: SlotGroupDefinition = {
  label: 'Card 2 Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'See The Schedule' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const feature3LinkDef: SlotGroupDefinition = {
  label: 'Card 3 Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'See Options' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const feature4LinkDef: SlotGroupDefinition = {
  label: 'Card 4 Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'See Rewards' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const feature6LinkDef: SlotGroupDefinition = {
  label: 'Card 6 Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Listen Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Conditional cards + band alternation
// ============================================================================

// Resident Events (card 2) and Spotify (card 6) only render on the live site
// once an operator sets a real destination. Empty, "#", the old placeholder
// defaults, and contact-page links all count as "not set yet".
function normalizeUrl(url: string | null | undefined): string {
  return (url ?? '').trim().toLowerCase().replace(/\/+$/, '');
}

function hasCustomLink(url: string | null | undefined, placeholders: string[]): boolean {
  const normalized = normalizeUrl(url);
  if (!normalized || normalized === '#') return false;
  if (/^(https?:\/\/[^/]+)?\/contact(-us)?([/?#]|$)/.test(normalized)) return false;
  return !placeholders.includes(normalized);
}

type BandTone = 'light' | 'primary';

// Light bands put the image left; primary bands put it right.
function bandStyles(tone: BandTone) {
  const light = tone === 'light';
  return {
    band: light ? 'bg-white' : 'bg-[var(--vibe-color-primary)]',
    media: light ? '' : 'md:order-2',
    mediaFallback: light
      ? 'bg-[var(--vibe-color-surface)]'
      : 'bg-[color-mix(in_srgb,var(--vibe-color-primary)_75%,black)]',
    copy: light ? 'md:pl-4' : 'md:order-1 md:pr-4',
    eyebrow: light
      ? 'text-[var(--vibe-color-primary)]'
      : 'text-[var(--vibe-color-text-on-primary)]',
    title: light ? 'text-[var(--vibe-color-text)]' : 'text-[var(--vibe-color-text-on-primary)]',
    body: light
      ? 'text-[var(--vibe-color-text-muted)]'
      : 'text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2',
    link: light ? 'text-[var(--vibe-color-primary)]' : 'text-[var(--vibe-color-text-on-primary)]',
  };
}

function HiddenCardNotice() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
      <p className="inline-block rounded bg-amber-100 px-3 py-1.5 font-body text-xs font-semibold text-amber-900">
        Hidden on the live site until a custom link is added (not the contact page).
      </p>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function LifestyleFeatures() {
  const img1 = useSlotGroup('lifestylefeatures.card1_image', feature1ImageDef);
  const img2 = useSlotGroup('lifestylefeatures.card2_image', feature2ImageDef);
  const img3 = useSlotGroup('lifestylefeatures.card3_image', feature3ImageDef);
  const img4 = useSlotGroup('lifestylefeatures.card4_image', feature4ImageDef);
  const img6 = useSlotGroup('lifestylefeatures.card6_image', feature6ImageDef);

  const link1 = useSlotGroup('lifestylefeatures.card1_link', feature1LinkDef);
  const link2 = useSlotGroup('lifestylefeatures.card2_link', feature2LinkDef);
  const link3 = useSlotGroup('lifestylefeatures.card3_link', feature3LinkDef);
  const link4 = useSlotGroup('lifestylefeatures.card4_link', feature4LinkDef);
  const link6 = useSlotGroup('lifestylefeatures.card6_link', feature6LinkDef);
  const readableOnPrimary = useReadableOnPrimary();
  const readableLinkOnWhite = useReadablePrimaryOnWhite('color4');
  const readableEyebrowOnWhite = useReadablePrimaryOnWhite('primary');
  const isEditMode = useEditMode()?.isEditMode ?? false;

  const card2HasLink = hasCustomLink(link2.url, ['/amenities']);
  const card6HasLink = hasCustomLink(link6.url, ['https://open.spotify.com']);
  const showCard2 = card2HasLink || isEditMode;
  const showCard6 = card6HasLink || isEditMode;

  // Tones are assigned from the bottom up so the last visible card is always
  // light — the LifestyleCTA section below it is a dark image band.
  const visibleCards = [1, showCard2 && 2, 3, 4, showCard6 && 6].filter(
    (n): n is number => typeof n === 'number',
  );
  const toneFor = (card: number): BandTone =>
    (visibleCards.length - 1 - visibleCards.indexOf(card)) % 2 === 0 ? 'light' : 'primary';
  const s1 = bandStyles(toneFor(1));
  const s2 = bandStyles(toneFor(2));
  const s3 = bandStyles(toneFor(3));
  const s4 = bandStyles(toneFor(4));
  const s6 = bandStyles(toneFor(6));
  const linkStyle = (tone: BandTone) => (tone === 'light' ? readableLinkOnWhite : undefined);
  const bandStyle = (tone: BandTone) => (tone === 'light' ? readableEyebrowOnWhite : readableOnPrimary);

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 160 });

  return (
    <section className="w-full">
      <div ref={gridRef}>
        {/* Card 1 — Better-Built Living */}
        <div data-reveal style={bandStyle(toneFor(1))} className={`reveal-fade-up ${s1.band} py-16 md:py-20`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className={`relative aspect-[4/3] md:aspect-[5/4] overflow-hidden ${s1.media}`}>
                <EditableSlotGroup groupPath="lifestylefeatures.card1_image" definition={feature1ImageDef}>
                  {img1.image ? (
                    <VibesitesImage url={img1.image} alt={img1.alt || ''} sizes="(min-width: 768px) 50vw, 100vw" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]" />
                  ) : (
                    <div className={`absolute inset-0 ${s1.mediaFallback}`} />
                  )}
                </EditableSlotGroup>
              </div>
              <div className={s1.copy}>
                <RichTextContent path="lifestylefeatures.card1_eyebrow" defaultValue="<p>Better-Built Living</p>" as="p" className={`font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] ${s1.eyebrow} mb-3`} />
                <RichTextContent path="lifestylefeatures.card1_title" defaultValue='<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>' as="h3" className={`font-heading text-2xl sm:text-3xl md:text-4xl font-normal ${s1.title} leading-tight tracking-tight mb-4`} />
                <RichTextContent path="lifestylefeatures.card1_body" defaultValue="<p>Our homes are built off-site using an advanced modular construction process — delivering tighter tolerances, stronger structures, and smarter building science than traditional stick-built apartments.</p>" as="div" className={`font-body text-base sm:text-lg leading-relaxed ${s1.body} [&_p]:mb-3 last:[&_p]:mb-0`} />
                <EditableSlotGroup groupPath="lifestylefeatures.card1_link" definition={feature1LinkDef}>
                  <a href={link1.url || '#'} target={link1._target || '_self'} style={linkStyle(toneFor(1))} className={`mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] ${s1.link} hover:gap-3 transition-all duration-200`}>
                    <span>{link1.label || 'Be Informed'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2 — Resident Events (live only with a custom link) */}
        {showCard2 && (
        <div data-reveal style={bandStyle(toneFor(2))} className={`reveal-fade-up ${s2.band} py-16 md:py-20`}>
          {!card2HasLink && <HiddenCardNotice />}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className={`relative aspect-[4/3] md:aspect-[5/4] overflow-hidden ${s2.media}`}>
                <EditableSlotGroup groupPath="lifestylefeatures.card2_image" definition={feature2ImageDef}>
                  {img2.image ? (
                    <VibesitesImage url={img2.image} alt={img2.alt || ''} sizes="(min-width: 768px) 50vw, 100vw" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]" />
                  ) : (
                    <div className={`absolute inset-0 ${s2.mediaFallback}`} />
                  )}
                </EditableSlotGroup>
              </div>
              <div className={s2.copy}>
                <RichTextContent path="lifestylefeatures.card2_eyebrow" defaultValue="<p>Resident Events</p>" as="p" className={`font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] ${s2.eyebrow} mb-3`} />
                <RichTextContent path="lifestylefeatures.card2_title" defaultValue="<p>See the Schedule of Events</p>" as="h3" className={`font-heading text-2xl sm:text-3xl md:text-4xl font-normal ${s2.title} leading-tight tracking-tight mb-4`} />
                <RichTextContent path="lifestylefeatures.card2_body" defaultValue='<p>Pizza nights, pool parties, holiday mixers — our resident calendar is packed with easy ways to meet neighbors and feel at home. Check the schedule for what&rsquo;s coming up at <span data-type="field-token" data-field="property_name"></span>.</p>' as="div" className={`font-body text-base sm:text-lg leading-relaxed ${s2.body} [&_p]:mb-3 last:[&_p]:mb-0`} />
                <EditableSlotGroup groupPath="lifestylefeatures.card2_link" definition={feature2LinkDef}>
                  <a href={link2.url || '#'} target={link2._target || '_self'} style={linkStyle(toneFor(2))} className={`mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] ${s2.link} hover:gap-3 transition-all duration-200`}>
                    <span>{link2.label || 'See The Schedule'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Card 3 — Flexible Living */}
        <div data-reveal style={bandStyle(toneFor(3))} className={`reveal-fade-up ${s3.band} py-16 md:py-20`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className={`relative aspect-[4/3] md:aspect-[5/4] overflow-hidden ${s3.media}`}>
                <EditableSlotGroup groupPath="lifestylefeatures.card3_image" definition={feature3ImageDef}>
                  {img3.image ? (
                    <VibesitesImage url={img3.image} alt={img3.alt || ''} sizes="(min-width: 768px) 50vw, 100vw" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]" />
                  ) : (
                    <div className={`absolute inset-0 ${s3.mediaFallback}`} />
                  )}
                </EditableSlotGroup>
              </div>
              <div className={s3.copy}>
                <RichTextContent path="lifestylefeatures.card3_eyebrow" defaultValue="<p>Flexible Living Programs</p>" as="p" className={`font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] ${s3.eyebrow} mb-3`} />
                <RichTextContent path="lifestylefeatures.card3_title" defaultValue="<p>Learn more about our Flexible Living Programs</p>" as="h3" className={`font-heading text-2xl sm:text-3xl md:text-4xl font-normal ${s3.title} leading-tight tracking-tight mb-4`} />
                <RichTextContent path="lifestylefeatures.card3_body" defaultValue="<p>Our Flexible Living Programs simplify renting with tools that reduce upfront costs, increase financial control, and add protection — like deposit alternatives, flexible rent dates, and move-in-ready furniture packages.</p>" as="div" className={`font-body text-base sm:text-lg leading-relaxed ${s3.body} [&_p]:mb-3 last:[&_p]:mb-0`} />
                <EditableSlotGroup groupPath="lifestylefeatures.card3_link" definition={feature3LinkDef}>
                  <a href={link3.url || '#'} target={link3._target || '_self'} style={linkStyle(toneFor(3))} className={`mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] ${s3.link} hover:gap-3 transition-all duration-200`}>
                    <span>{link3.label || 'See Options'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4 — Rewards */}
        <div data-reveal style={bandStyle(toneFor(4))} className={`reveal-fade-up ${s4.band} py-16 md:py-20`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className={`relative aspect-[4/3] md:aspect-[5/4] overflow-hidden ${s4.media}`}>
                <EditableSlotGroup groupPath="lifestylefeatures.card4_image" definition={feature4ImageDef}>
                  {img4.image ? (
                    <VibesitesImage url={img4.image} alt={img4.alt || ''} sizes="(min-width: 768px) 50vw, 100vw" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]" />
                  ) : (
                    <div className={`absolute inset-0 ${s4.mediaFallback}`} />
                  )}
                </EditableSlotGroup>
              </div>
              <div className={s4.copy}>
                <RichTextContent path="lifestylefeatures.card4_eyebrow" defaultValue="<p>Resident Rewards</p>" as="p" className={`font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] ${s4.eyebrow} mb-3`} />
                <RichTextContent path="lifestylefeatures.card4_title" defaultValue="<p>Learn more about our Rewards Programs</p>" as="h3" className={`font-heading text-2xl sm:text-3xl md:text-4xl font-normal ${s4.title} leading-tight tracking-tight mb-4`} />
                <RichTextContent path="lifestylefeatures.card4_body" defaultValue="<p>Living here comes with more than great amenities — it comes with real opportunities to earn. Build credit through rent, bank gift cards for on-time payments, and unlock perks from local partners.</p>" as="div" className={`font-body text-base sm:text-lg leading-relaxed ${s4.body} [&_p]:mb-3 last:[&_p]:mb-0`} />
                <EditableSlotGroup groupPath="lifestylefeatures.card4_link" definition={feature4LinkDef}>
                  <a href={link4.url || '#'} target={link4._target || '_self'} style={linkStyle(toneFor(4))} className={`mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] ${s4.link} hover:gap-3 transition-all duration-200`}>
                    <span>{link4.label || 'See Rewards'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* Card 6 — Spotify Channel (live only with a custom link) */}
        {showCard6 && (
        <div data-reveal style={bandStyle(toneFor(6))} className={`reveal-fade-up ${s6.band} py-16 md:py-20`}>
          {!card6HasLink && <HiddenCardNotice />}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
              <div className={`relative aspect-[4/3] md:aspect-[5/4] overflow-hidden ${s6.media}`}>
                <EditableSlotGroup groupPath="lifestylefeatures.card6_image" definition={feature6ImageDef}>
                  {img6.image ? (
                    <VibesitesImage url={img6.image} alt={img6.alt || ''} sizes="(min-width: 768px) 50vw, 100vw" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]" />
                  ) : (
                    <div className={`absolute inset-0 ${s6.mediaFallback}`} />
                  )}
                </EditableSlotGroup>
              </div>
              <div className={s6.copy}>
                <RichTextContent path="lifestylefeatures.card6_eyebrow" defaultValue="<p>Spotify Channel</p>" as="p" className={`font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] ${s6.eyebrow} mb-3`} />
                <RichTextContent path="lifestylefeatures.card6_title" defaultValue="<p>Listen to Our Spotify Channel</p>" as="h3" className={`font-heading text-2xl sm:text-3xl md:text-4xl font-normal ${s6.title} leading-tight tracking-tight mb-4`} />
                <RichTextContent path="lifestylefeatures.card6_body" defaultValue='<p>Curated playlists for every mood — from pool-day sun to Sunday-morning slow. Follow <span data-type="field-token" data-field="property_name"></span> on Spotify and let us set the soundtrack.</p>' as="div" className={`font-body text-base sm:text-lg leading-relaxed ${s6.body} [&_p]:mb-3 last:[&_p]:mb-0`} />
                <EditableSlotGroup groupPath="lifestylefeatures.card6_link" definition={feature6LinkDef}>
                  <a href={link6.url || '#'} target={link6._target || '_self'} style={linkStyle(toneFor(6))} className={`mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] ${s6.link} hover:gap-3 transition-all duration-200`}>
                    <span>{link6.label || 'Listen Now'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleFeatures',
  displayName: 'Lifestyle Features',
  tokens: {
    'lifestylefeatures.card1_eyebrow': {
      label: 'Card 1 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Better-Built Living</p>',
    },
    'lifestylefeatures.card1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>',
    },
    'lifestylefeatures.card1_body': {
      label: 'Card 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our homes are built off-site using an advanced modular construction process — delivering tighter tolerances, stronger structures, and smarter building science than traditional stick-built apartments.</p>',
    },
    'lifestylefeatures.card2_eyebrow': {
      label: 'Card 2 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Resident Events</p>',
    },
    'lifestylefeatures.card2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>See the Schedule of Events</p>',
    },
    'lifestylefeatures.card2_body': {
      label: 'Card 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Pizza nights, pool parties, holiday mixers — our resident calendar is packed with easy ways to meet neighbors and feel at home. Check the schedule for what&rsquo;s coming up at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'lifestylefeatures.card3_eyebrow': {
      label: 'Card 3 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Flexible Living Programs</p>',
    },
    'lifestylefeatures.card3_title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Learn more about our Flexible Living Programs</p>',
    },
    'lifestylefeatures.card3_body': {
      label: 'Card 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our Flexible Living Programs simplify renting with tools that reduce upfront costs, increase financial control, and add protection — like deposit alternatives, flexible rent dates, and move-in-ready furniture packages.</p>',
    },
    'lifestylefeatures.card4_eyebrow': {
      label: 'Card 4 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Resident Rewards</p>',
    },
    'lifestylefeatures.card4_title': {
      label: 'Card 4 Title',
      type: 'richtext',
      defaultValue: '<p>Learn more about our Rewards Programs</p>',
    },
    'lifestylefeatures.card4_body': {
      label: 'Card 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Living here comes with more than great amenities — it comes with real opportunities to earn. Build credit through rent, bank gift cards for on-time payments, and unlock perks from local partners.</p>',
    },
    'lifestylefeatures.card6_eyebrow': {
      label: 'Card 6 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Spotify Channel</p>',
    },
    'lifestylefeatures.card6_title': {
      label: 'Card 6 Title',
      type: 'richtext',
      defaultValue: '<p>Listen to Our Spotify Channel</p>',
    },
    'lifestylefeatures.card6_body': {
      label: 'Card 6 Body',
      type: 'richtext',
      defaultValue:
        '<p>Curated playlists for every mood — from pool-day sun to Sunday-morning slow. Follow <span data-type="field-token" data-field="property_name"></span> on Spotify and let us set the soundtrack.</p>',
    },
  },
  slotGroups: {
    'lifestylefeatures.card1_image': feature1ImageDef,
    'lifestylefeatures.card2_image': feature2ImageDef,
    'lifestylefeatures.card3_image': feature3ImageDef,
    'lifestylefeatures.card4_image': feature4ImageDef,
    'lifestylefeatures.card6_image': feature6ImageDef,
    'lifestylefeatures.card1_link': feature1LinkDef,
    'lifestylefeatures.card2_link': feature2LinkDef,
    'lifestylefeatures.card3_link': feature3LinkDef,
    'lifestylefeatures.card4_link': feature4LinkDef,
    'lifestylefeatures.card6_link': feature6LinkDef,
  },
};

(LifestyleFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleFeatures;
